import {
  planWithStarts,
  type ChatEvent,
  type ChatSnapshot,
  type MessageView,
  type SequencedEvent,
  type StreamFrame,
} from "./chat";

/**
 * Folds a chat's snapshot and its stream events into the state a screen draws, so that a refresh,
 * a second tab and a reconnect agree. The server's tests and the web import this one file. It is
 * pure and browser-safe: it imports nothing at run time, and it never changes what it is given.
 *
 * The rule it keeps: a snapshot taken at cursor `c`, with the events after `c` applied, equals the
 * snapshot taken later (apart from `run.stopping`, which no event carries). Four things follow.
 *
 * - **Numbering.** Messages and events share one sequence, so the numbers of the events a client
 *   sees have holes. An event is applied only when its number is above the last one applied, so a
 *   repeat or a stream that resumes early changes nothing; a jump in the numbers is not a gap.
 * - **A gap** is an event that names something the state does not hold: text for a message that is
 *   not being written, the end of an unknown step. The state is then left exactly as it was and
 *   `gap` is true, so the caller fetches the snapshot again. Removing something that is not there
 *   (a request that closed, a queued message that left) is not a gap: the state already is what
 *   the event describes. Nor is a step on a message the state lacks (see the next item).
 * - **A turn that only called tools** says nothing, so a snapshot leaves its row out until a step,
 *   a note, a plan or a source is on it. The executor writes such a row whole (`TEXT_MESSAGE_START`,
 *   `TEXT_MESSAGE_END`) and the step, note, plan or source joins it afterwards (`TOOL_CALL_START`,
 *   `note.added`, `plan.updated`, `source.added`), so the state does the same: a message that ends
 *   with no text, no step, no note, no plan and no source is not kept, and a step, note, plan or
 *   source that names a message the state does not hold brings it back, complete and with no
 *   words. A reader whose snapshot was taken between the row and its step meets the same
 *   step, so the state cannot tell it from a step on a message that never existed; it trusts the
 *   stream, and the next snapshot sets right anything that missed. A note or source the message
 *   already shows is not added again, so a replayed event adds one.
 * - **The snapshot is folded.** `applySnapshot` applies its `inflight` events to the message being
 *   written and then drops them, so the state holds that message in `messages`, with its text so
 *   far, and `snapshot.cursor` always equals `lastSeq`. The snapshot's row for that message holds
 *   no text and may already show its steps, so a message or step that is already there is not
 *   added again.
 * - **What an event cannot say, the state works out.** None of these events says a message was
 *   interrupted, a step went unknown or a request's mark moved, so the reducer applies the rules
 *   the store applies:
 *   - when a run finishes or fails (a stopped run finishes), a message still being written was cut
 *     off and becomes `interrupted`, keeping its text. So a message is only `complete` if it
 *     received `TEXT_MESSAGE_END`; an executor must not send one for a partial message;
 *   - when a run starts (`RUN_STARTED`, whatever its `attempt`), whoever wrote before it is gone:
 *     a message still being written becomes `interrupted` and a running step `unknown`. That is a
 *     takeover, a run handed back at shutdown (which gives its attempt back, so it starts again at
 *     the same number, with nothing on the stream in between), or a step an earlier run left. A
 *     run that ends leaves its steps alone, so the executor must end each one itself;
 *   - the message that asked a question carries its request's `requestId` only while the request
 *     is pending: `request.created` marks the latest assistant message and any closing
 *     event clears the mark and takes the request off the list, which holds pending requests only.
 *     Two pending requests raised from one message leave the mark with the newer one.
 *
 * `chat` is the list's view of the chat. Apart from `title` (Abacor's `title.changed`),
 * `visibility` with its `visibilityRevision`, and `needsYou` (true for the owner while any request
 * is pending), it changes only with a new snapshot: the preview and the times arrive with the
 * list, not the stream.
 *
 * A message the stream opens (`TEXT_MESSAGE_START`), or a step, note or plan brings back, has the event's number
 * as its `seq` and the time of the message before it as its `at`, because the event carries
 * neither; both are right again from the next snapshot. The queue is the owner's alone: a viewer's
 * state ignores it.
 */
export type ChatState = { snapshot: ChatSnapshot; lastSeq: number };

/**
 * Starts from a snapshot as the server sent it, with its in-flight events folded in. An in-flight
 * event that names something the snapshot does not hold is skipped; the stream's own events decide
 * from there. The state resumes after the snapshot's cursor, or after the last in-flight event if
 * that is later, so no event already folded in is applied twice.
 */
export function applySnapshot(snapshot: ChatSnapshot): ChatState {
  let folded: ChatSnapshot = { ...snapshot, inflight: [] };
  let lastSeq = snapshot.cursor;
  for (const { seq, event } of snapshot.inflight) {
    folded = reduce(folded, event, seq) ?? folded;
    lastSeq = Math.max(lastSeq, seq);
  }
  return { snapshot: { ...folded, cursor: lastSeq }, lastSeq };
}

/**
 * Applies the events above `state.lastSeq`, in order. If any of them names something the state
 * does not hold, nothing is applied, the same `state` is returned and `gap` is true.
 *
 * `through` is the cursor the batch covers (a frame's `lastSeq`): the server leaves out of a batch
 * the events a reader may not see, such as the owner's queue for a viewer, so a batch can cover
 * numbers past its last event, or be empty. Once the events are applied, the position moves to
 * `through` when that is further on, so a reconnect resumes after what was covered rather than
 * asking for it again. Without it the position is the last event applied.
 */
export function applyEvents(
  state: ChatState,
  events: readonly SequencedEvent[],
  through?: number,
): { state: ChatState; gap: boolean } {
  let snapshot = state.snapshot;
  let lastSeq = state.lastSeq;
  for (const { seq, event } of events) {
    if (seq <= lastSeq) continue;
    const next = reduce(snapshot, event, seq);
    if (next === null) return { state, gap: true };
    snapshot = next;
    lastSeq = seq;
  }
  if (through !== undefined && through > lastSeq) lastSeq = through;
  if (lastSeq === state.lastSeq) return { state, gap: false };
  return { state: { snapshot: { ...snapshot, cursor: lastSeq }, lastSeq }, gap: false };
}

/**
 * Applies a stream batch as a whole: its events, then the cursor it covers (`applyEvents` with the
 * frame's `lastSeq`), so a caller cannot forget the cursor. A `reset` frame is not the reducer's:
 * it says the cursor is no use any more, and the caller fetches the snapshot again.
 */
export function applyFrame(
  state: ChatState,
  frame: Extract<StreamFrame, { kind: "batch" }>,
): { state: ChatState; gap: boolean } {
  return applyEvents(state, frame.events, frame.lastSeq);
}

/** The snapshot with one event applied, or null when the event names something it does not hold. */
function reduce(snapshot: ChatSnapshot, event: ChatEvent, seq: number): ChatSnapshot | null {
  switch (event.type) {
    case "RUN_STARTED":
      return {
        ...snapshot,
        run: { id: event.runId, status: "running", stopping: false },
        // A thread has one active run and nothing is written before its start, so whatever is
        // still being written was left by a holder that is gone: a takeover, or a run handed back
        // at shutdown, which gives its attempt back and so starts again at the same number.
        messages: unsettled(interrupted(snapshot.messages)),
      };
    case "RUN_FINISHED":
      return {
        ...snapshot,
        run: {
          id: event.runId,
          status: "finished",
          stopping: false,
          ...requestOf(snapshot, event.runId),
        },
        messages: unsettledWork(interrupted(snapshot.messages)),
      };
    case "RUN_ERROR":
      // The only end that says why: the run view of a failed run holds the code, as a snapshot's.
      return {
        ...snapshot,
        run: {
          id: event.runId,
          status: "finished",
          stopping: false,
          errorCode: event.code,
          ...requestOf(snapshot, event.runId),
        },
        messages: unsettledWork(interrupted(snapshot.messages)),
      };
    case "TEXT_MESSAGE_START": {
      if (indexOfMessage(snapshot, event.messageId) !== -1) return snapshot;
      return {
        ...snapshot,
        messages: insertInOrder(snapshot.messages, {
          id: event.messageId,
          seq,
          at: snapshot.messages.at(-1)?.at ?? snapshot.chat.lastAt,
          status: "streaming",
          author: { kind: "assistant" },
          ...runOf(snapshot),
          text: "",
          steps: [],
        }),
      };
    }
    case "TEXT_MESSAGE_CONTENT": {
      const written = writtenMessage(snapshot, event.messageId);
      if (!written) return null;
      return replaceMessage(snapshot, written.index, {
        ...written.message,
        text: written.message.text + event.delta,
      });
    }
    case "TEXT_MESSAGE_END": {
      const written = writtenMessage(snapshot, event.messageId);
      if (!written) return null;
      // A turn that only called tools has nothing to show until a step, a note, a plan or a source
      // is on it: a snapshot leaves its row out, so the state does too, and the step, note, plan or
      // source brings it back.
      if (
        written.message.text === "" &&
        written.message.steps.length === 0 &&
        (written.message.notes ?? []).length === 0 &&
        written.message.plan === undefined &&
        (written.message.sources ?? []).length === 0
      ) {
        return {
          ...snapshot,
          messages: snapshot.messages.filter((_, position) => position !== written.index),
        };
      }
      // The code it was writing is whole now: its line of work comes with the turn's end.
      return replaceMessage(snapshot, written.index, {
        ...withoutWriting(written.message),
        status: "complete",
      });
    }
    case "MESSAGE_FILES": {
      // The files Abacor made, shown on the message that tells of them. That message ended with
      // words, so it is in the state; one that is not is a gap.
      const index = indexOfMessage(snapshot, event.messageId);
      const message = snapshot.messages[index];
      if (!message) return null;
      return replaceMessage(snapshot, index, { ...message, files: event.files });
    }
    case "TOOL_CALL_START": {
      const index = indexOfMessage(snapshot, event.parentMessageId);
      const parent = snapshot.messages[index];
      if (!parent) {
        // The row of a turn that only called tools, written whole and left out until now.
        return {
          ...snapshot,
          messages: insertInOrder(snapshot.messages, {
            id: event.parentMessageId,
            seq,
            at: snapshot.messages.at(-1)?.at ?? snapshot.chat.lastAt,
            status: "complete",
            author: { kind: "assistant" },
            ...runOf(snapshot),
            text: "",
            steps: [event.step],
          }),
        };
      }
      if (parent.steps.some((existing) => existing.id === event.step.id)) return snapshot;
      return replaceMessage(snapshot, index, { ...parent, steps: [...parent.steps, event.step] });
    }
    case "TOOL_CALL_END": {
      // The step's own message when the end names it; an end stored before it did settles the
      // newest step with that ID.
      const holds = (candidate: MessageView) =>
        candidate.steps.some((existing) => existing.id === event.toolCallId);
      const index =
        event.parentMessageId === undefined
          ? snapshot.messages.findLastIndex(holds)
          : snapshot.messages.findIndex(
              (candidate) => candidate.id === event.parentMessageId && holds(candidate),
            );
      const holder = snapshot.messages[index];
      if (!holder) return null;
      const steps = holder.steps.map((existing) =>
        existing.id === event.toolCallId ? { ...existing, status: event.status } : existing,
      );
      return replaceMessage(snapshot, index, { ...holder, steps });
    }
    case "CUSTOM":
      return settledDrafting(reduceCustom(snapshot, event, seq), event);
  }
}

/**
 * The edit Abacor was writing ends with its own call's result (`draft.written`, said after the
 * batch it landed), or with that call's failed line. A turn can write two edits before either
 * runs, so an earlier call's result leaves a later call's marks alone. A run's start or end makes
 * a new run view, which has none.
 */
function settledDrafting(
  snapshot: ChatSnapshot,
  event: Extract<ChatEvent, { type: "CUSTOM" }>,
): ChatSnapshot {
  const run = snapshot.run;
  if (!run?.drafting) return snapshot;
  const ended =
    (event.name === "draft.written" && event.value.callId === run.drafting.callId) ||
    (event.name === "work.set" &&
      event.value.work.kind === "edit" &&
      event.value.work.id === run.drafting.callId);
  if (!ended) return snapshot;
  const { drafting: _ended, ...rest } = run;
  return { ...snapshot, run: rest };
}

function reduceCustom(
  snapshot: ChatSnapshot,
  event: Extract<ChatEvent, { type: "CUSTOM" }>,
  seq: number,
): ChatSnapshot {
  switch (event.name) {
    case "message":
      if (indexOfMessage(snapshot, event.value.id) !== -1) return snapshot;
      return { ...snapshot, messages: insertInOrder(snapshot.messages, event.value) };
    case "queue.changed": {
      if (snapshot.viewer !== "owner") return snapshot;
      const change = event.value;
      if (change.change === "added") {
        if (snapshot.queue.some((item) => item.id === change.item.id)) return snapshot;
        return { ...snapshot, queue: [...snapshot.queue, change.item] };
      }
      return {
        ...snapshot,
        queue: snapshot.queue.filter((item) => item.id !== change.inboxItemId),
      };
    }
    case "request.created": {
      const raised = event.value;
      if (raised.status !== "pending" || snapshot.requests.some((r) => r.id === raised.id)) {
        return snapshot;
      }
      const asking = snapshot.messages.findLastIndex((m) => m.author.kind === "assistant");
      const asker = snapshot.messages[asking];
      const requests = [...snapshot.requests, raised];
      const marked = asker
        ? replaceMessage(snapshot, asking, { ...asker, requestId: raised.id })
        : snapshot;
      return {
        ...marked,
        requests,
        chat: { ...snapshot.chat, needsYou: needsYou(snapshot.viewer, requests) },
      };
    }
    case "request.answered":
    case "request.expired":
    case "request.canceled": {
      const closedId = event.value.requestId;
      const requests = snapshot.requests.filter((r) => r.id !== closedId);
      return {
        ...snapshot,
        requests,
        messages: snapshot.messages.map((m) => (m.requestId === closedId ? withoutRequest(m) : m)),
        chat: { ...snapshot.chat, needsYou: needsYou(snapshot.viewer, requests) },
      };
    }
    case "visibility.changed":
      return {
        ...snapshot,
        chat: {
          ...snapshot.chat,
          visibility: event.value.visibility,
          visibilityRevision: event.value.revision,
        },
      };
    case "run.request": {
      // Only the run that is the snapshot's: one that ended since has no rows to come.
      const { runId, requestRunId } = event.value;
      if (snapshot.run?.id !== runId) return snapshot;
      return { ...snapshot, run: { ...snapshot.run, requestRunId } };
    }
    case "run.queued":
      return { ...snapshot, run: { id: event.value.runId, status: "queued", stopping: false } };
    case "title.changed":
      return { ...snapshot, chat: { ...snapshot.chat, title: event.value.title } };
    case "note.added": {
      const { messageId, note } = event.value;
      const index = indexOfMessage(snapshot, messageId);
      const parent = snapshot.messages[index];
      if (!parent) {
        // The row of a turn that only called `note`, written whole and left out until now.
        return {
          ...snapshot,
          messages: insertInOrder(snapshot.messages, {
            id: messageId,
            seq,
            at: snapshot.messages.at(-1)?.at ?? snapshot.chat.lastAt,
            status: "complete",
            author: { kind: "assistant" },
            ...runOf(snapshot),
            text: "",
            steps: [],
            notes: [note],
          }),
        };
      }
      const held = parent.notes ?? [];
      if (held.some((existing) => existing.id === note.id)) return snapshot;
      return replaceMessage(snapshot, index, { ...parent, notes: [...held, note] });
    }
    case "plan.updated": {
      const { messageId, plan } = event.value;
      // A plan is written by the run that is working, so its request is the run's.
      const requestRunId = snapshot.run?.requestRunId;
      const index = indexOfMessage(snapshot, messageId);
      const parent = snapshot.messages[index];
      // The message holds the request's first plan, and an earlier page may hold it: the card is
      // then drawn where the request's plan already shows, and is updated there. A snapshot taken
      // now draws it on the request's earliest turn its page holds, which is the same message
      // until new rows push that one off the page; either way the request has one card.
      const standIn = parent === undefined ? standInFor(snapshot, requestRunId) : -1;
      const held = snapshot.messages[standIn];
      if (held) {
        return replaceMessage(snapshot, standIn, {
          ...held,
          plan: planWithStarts(held.plan, plan),
        });
      }
      if (!parent) {
        // The row of a turn that only called `plan`, written whole and left out until now.
        return {
          ...snapshot,
          messages: insertInOrder(snapshot.messages, {
            id: messageId,
            seq,
            at: snapshot.messages.at(-1)?.at ?? snapshot.chat.lastAt,
            status: "complete",
            author: { kind: "assistant" },
            ...runOf(snapshot),
            text: "",
            steps: [],
            plan: planWithStarts(undefined, plan),
          }),
        };
      }
      // Each event carries the whole plan, so it replaces the last and a repeat changes nothing.
      return replaceMessage(snapshot, index, {
        ...parent,
        plan: planWithStarts(parent.plan, plan),
      });
    }
    case "work.set": {
      const { messageId, work } = event.value;
      const index = indexOfMessage(snapshot, messageId);
      const parent = snapshot.messages[index];
      if (!parent) {
        // The row of a turn that only read a file or ran code, written whole and left out until now.
        return {
          ...snapshot,
          messages: insertInOrder(snapshot.messages, {
            id: messageId,
            seq,
            at: snapshot.messages.at(-1)?.at ?? snapshot.chat.lastAt,
            status: "complete",
            author: { kind: "assistant" },
            ...runOf(snapshot),
            text: "",
            steps: [],
            work: [work],
          }),
        };
      }
      const held = parent.work ?? [];
      const at = held.findIndex((existing) => existing.id === work.id);
      return replaceMessage(snapshot, index, {
        ...parent,
        work: at === -1 ? [...held, work] : held.map((existing, i) => (i === at ? work : existing)),
      });
    }
    case "work.writing": {
      // Only a turn still being written can be writing code. One that has ended already (a
      // replay) has its lines of work, so the event changes nothing, and it is no gap.
      const written = writtenMessage(snapshot, event.value.messageId);
      if (!written || written.message.writing !== undefined) return snapshot;
      return replaceMessage(snapshot, written.index, {
        ...written.message,
        writing: event.value.at,
      });
    }
    case "draft.written":
      // Ends the edit being written, if it is this call's (`settledDrafting`).
      return snapshot;
    case "draft.writing": {
      // Only a run still working writes an edit: one that has ended (a replay) changes nothing.
      const run = snapshot.run;
      if (!run || run.status === "finished") return snapshot;
      return { ...snapshot, run: { ...run, drafting: event.value } };
    }
    case "process.changed": {
      // The batch goes on the turn that landed it, for its change card; the draft itself is the
      // draft stream's, so nothing else changes.
      const { messageId, ...change } = event.value;
      const index = indexOfMessage(snapshot, messageId);
      const parent = snapshot.messages[index];
      if (!parent) {
        // The row of a turn that only edited the draft, written whole and left out until now.
        return {
          ...snapshot,
          messages: insertInOrder(snapshot.messages, {
            id: messageId,
            seq,
            at: snapshot.messages.at(-1)?.at ?? snapshot.chat.lastAt,
            status: "complete",
            author: { kind: "assistant" },
            ...runOf(snapshot),
            text: "",
            steps: [],
            processChanges: [change],
          }),
        };
      }
      const held = parent.processChanges ?? [];
      if (held.some((existing) => existing.batchId === change.batchId)) return snapshot;
      return replaceMessage(snapshot, index, { ...parent, processChanges: [...held, change] });
    }
    case "try.started": {
      // Like a draft's batch: the try goes on the turn that started it, for its card.
      const { messageId, ...tried } = event.value;
      const index = indexOfMessage(snapshot, messageId);
      const parent = snapshot.messages[index];
      if (!parent) {
        return {
          ...snapshot,
          messages: insertInOrder(snapshot.messages, {
            id: messageId,
            seq,
            at: snapshot.messages.at(-1)?.at ?? snapshot.chat.lastAt,
            status: "complete",
            author: { kind: "assistant" },
            ...runOf(snapshot),
            text: "",
            steps: [],
            tries: [tried],
          }),
        };
      }
      const held = parent.tries ?? [];
      if (held.some((existing) => existing.caseId === tried.caseId)) return snapshot;
      return replaceMessage(snapshot, index, { ...parent, tries: [...held, tried] });
    }
    case "table.changed": {
      // Like a draft's batch: the table's change goes on the turn that landed it, for its card.
      const { messageId, ...change } = event.value;
      const index = indexOfMessage(snapshot, messageId);
      const parent = snapshot.messages[index];
      if (!parent) {
        return {
          ...snapshot,
          messages: insertInOrder(snapshot.messages, {
            id: messageId,
            seq,
            at: snapshot.messages.at(-1)?.at ?? snapshot.chat.lastAt,
            status: "complete",
            author: { kind: "assistant" },
            ...runOf(snapshot),
            text: "",
            steps: [],
            tableChanges: [change],
          }),
        };
      }
      const held = parent.tableChanges ?? [];
      if (
        held.some(
          (existing) => existing.callId === change.callId && existing.tableId === change.tableId,
        )
      )
        return snapshot;
      return replaceMessage(snapshot, index, { ...parent, tableChanges: [...held, change] });
    }
    case "source.added": {
      const { messageId, source } = event.value;
      const index = indexOfMessage(snapshot, messageId);
      const parent = snapshot.messages[index];
      if (!parent) {
        // The row of a turn that only called `read_file`, written whole and left out until now.
        return {
          ...snapshot,
          messages: insertInOrder(snapshot.messages, {
            id: messageId,
            seq,
            at: snapshot.messages.at(-1)?.at ?? snapshot.chat.lastAt,
            status: "complete",
            author: { kind: "assistant" },
            ...runOf(snapshot),
            text: "",
            steps: [],
            sources: [source],
          }),
        };
      }
      const held = parent.sources ?? [];
      if (held.some((existing) => existing.key === source.key)) return snapshot;
      return replaceMessage(snapshot, index, { ...parent, sources: [...held, source] });
    }
  }
}

/**
 * The run a row the stream inserts was written in, and the request it carries on: the snapshot's
 * current run, which the events of a run come after (`RUN_STARTED`). Left out when the snapshot
 * has none, or does not know the request.
 */
const runOf = (snapshot: ChatSnapshot): { runId?: string; requestRunId?: string } =>
  snapshot.run === null
    ? {}
    : {
        runId: snapshot.run.id,
        ...(snapshot.run.requestRunId === undefined
          ? {}
          : { requestRunId: snapshot.run.requestRunId }),
      };

/** The request run `runId` carries on, as the state already knows it: an end does not say. */
const requestOf = (snapshot: ChatSnapshot, runId: string): { requestRunId?: string } =>
  snapshot.run?.id === runId && snapshot.run.requestRunId !== undefined
    ? { requestRunId: snapshot.run.requestRunId }
    : {};

/**
 * Where a request's plan is drawn when the message that holds its first is not in the state: the
 * request's message that already shows the plan (a snapshot puts it on the request's earliest
 * message it holds when the first is on an earlier page). -1 when none does: this is then the
 * request's first plan, and the message named is new, inserted as before.
 */
function standInFor(snapshot: ChatSnapshot, requestRunId: string | undefined): number {
  if (requestRunId === undefined) return -1;
  return snapshot.messages.findIndex(
    (m) => m.author.kind === "assistant" && m.requestRunId === requestRunId && m.plan !== undefined,
  );
}

/** A request names the person it is waiting for, and in a chat that is always its owner. */
const needsYou = (viewer: ChatSnapshot["viewer"], requests: ChatSnapshot["requests"]) =>
  viewer === "owner" && requests.some((r) => r.status === "pending");

const indexOfMessage = (snapshot: ChatSnapshot, id: string) =>
  snapshot.messages.findIndex((m) => m.id === id);

/** The message that text can be added to or ended on: one that is still being written. */
function writtenMessage(snapshot: ChatSnapshot, id: string) {
  const index = indexOfMessage(snapshot, id);
  const message = snapshot.messages[index];
  return message?.status === "streaming" ? { index, message } : null;
}

const replaceMessage = (
  snapshot: ChatSnapshot,
  index: number,
  message: MessageView,
): ChatSnapshot => ({
  ...snapshot,
  messages: snapshot.messages.map((existing, position) =>
    position === index ? message : existing,
  ),
});

/** Keeps the transcript in order of `seq`, as a snapshot lists it; a tie goes after what is there. */
function insertInOrder(messages: readonly MessageView[], added: MessageView): MessageView[] {
  const before = messages.findIndex((m) => m.seq > added.seq);
  return before === -1
    ? [...messages, added]
    : [...messages.slice(0, before), added, ...messages.slice(before)];
}

/** A message that was still being written when its run ended was cut off, with any code it was writing. */
const interrupted = (messages: readonly MessageView[]): MessageView[] =>
  messages.map((m) =>
    m.status === "streaming" ? { ...withoutWriting(m), status: "interrupted" } : m,
  );

/** The message without the code it was writing: a turn that ended is writing nothing. */
function withoutWriting(message: MessageView): MessageView {
  const { writing: _ended, ...rest } = message;
  return rest;
}

/**
 * A line of work still running when its run ends was not seen to end: the call's result never came
 * (the run was stopped, or failed), which is what a reload shows too. Steps are settled by the
 * executor as the run ends; these are only told when the call starts and when its result lands.
 */
const unsettledWork = (messages: readonly MessageView[]): MessageView[] =>
  messages.map((m) =>
    m.work?.some((w) => w.status === "running")
      ? {
          ...m,
          work: m.work.map((w) =>
            w.status === "running" ? { ...w, status: "unknown" as const } : w,
          ),
        }
      : m,
  );

/** A step that was running under an attempt that is gone ended in a way nobody saw. */
const unsettled = (messages: readonly MessageView[]): MessageView[] =>
  unsettledWork(
    messages.map((m) =>
      m.steps.some((s) => s.status === "running")
        ? {
            ...m,
            steps: m.steps.map((s) => (s.status === "running" ? { ...s, status: "unknown" } : s)),
          }
        : m,
    ),
  );

function withoutRequest(message: MessageView): MessageView {
  const { requestId: _closed, ...rest } = message;
  return rest;
}
